/**
 * 플랫폼 계정 축(auth-service) — "누구인가".
 *
 * @db/auth/enums/user.enum.ts 의 미러다. BE enum 이 바뀌면 여기부터 고친다.
 * ⚠️ admin-service 의 role(SUPER_ADMIN/ADMIN)과는 **완전히 다른 축**이다. 섞지 말 것.
 */

/**
 * 사용자 유형 — UserType. **가입 시점에 확정되는 불변값**이다.
 * 개인↔기업 전환 경로는 없다 — 두 세계는 계정 생성 시점에 갈라진다.
 */
export const UserType = {
  INDIVIDUAL: 'INDIVIDUAL',
  BUSINESS: 'BUSINESS',
} as const;
export type UserType = (typeof UserType)[keyof typeof UserType];

/** 계정 상태 — UserStatus */
export const UserStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  /** 첫 로그인 온보딩(비밀번호 변경 → 이메일 인증 → 휴대폰 인증) 미완료. */
  PENDING_ONBOARDING: 'PENDING_ONBOARDING',
  SUSPENDED: 'SUSPENDED',
  WITHDRAWN: 'WITHDRAWN',
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

export const USER_STATUS_LABEL = {
  ACTIVE: '활성',
  INACTIVE: '비활성',
  PENDING_ONBOARDING: '최초 설정 대기',
  SUSPENDED: '정지',
  WITHDRAWN: '탈퇴',
} as const satisfies Record<UserStatus, string>;

/** 권한 — UserRole. USER | PARTNER | MANAGER | ADMIN | SUPER_ADMIN */
export const UserRole = {
  USER: 'USER',
  PARTNER: 'PARTNER',
  MANAGER: 'MANAGER',
  ADMIN: 'ADMIN',
  SUPER_ADMIN: 'SUPER_ADMIN',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

/**
 * AccountService.findById 가 반환하는 User 엔티티(비밀번호 제외).
 * BaseAuditEntity 필드는 UI에서 쓸 일이 적으므로 선택적으로만 노출.
 */
/** 재동의·온보딩 동의 대상 필수 문서 키 — 서버 `REQUIRED_ACCOUNT_CONSENT_KEYS` 와 같다. */
export type RequiredConsentKey = 'terms' | 'privacy';

export interface AuthUser {
  id: string;
  email: string;
  /** 완성형 로그인 식별자 (개인: `odin` / 기업: `odin@howcost`). */
  loginId: string;
  /** 소속 회사코드. 개인 계정은 빈 문자열. */
  orgSlug: string;
  /** 온보딩 판정 — 발급받은 비밀번호를 아직 안 바꿨는가. */
  mustChangePassword: boolean;
  emailVerified: boolean;
  name: string;
  nickname: string | null;
  userRole: UserRole;
  userType: UserType;
  status: UserStatus;
  phone: string | null;
  phoneVerified: boolean;
  /** 휴대폰 인증 유예 마감(UTC ISO) — 미인증 이관 공유 계정만, 그 외 null. */
  phoneVerifyDueAt: string | null;
  avatarUrl: string | null;
  marketingConsent: boolean;
  eventConsent: boolean;
  /**
   * 약관 개정으로 다시 동의해야 하는 필수 문서(`terms`·`privacy`). 비어 있으면 대상 아님.
   * `/auth/me` 는 이 이름, 로그인 응답(축약형)은 `reconsent` 로 온다.
   */
  reconsentRequired?: RequiredConsentKey[];
  reconsent?: RequiredConsentKey[];
  lastLoginAt: string | null; // ISO 8601
  createdAt?: string;
  updatedAt?: string;
}
